import {
  OmnisearchPair,
  serializeOmnisearch,
  splitOmnisearch,
} from "components/Omnisearch/omnisearchPills";
import { CALENDAR_FEED_RESERVED_KEYS } from "encamp-shared/src/tasks/calendarFeedSearch";
import { TaskStatus } from "generated-graphql/graphql";

/** The filters a custom calendar subscription can be built from. */
export type CalendarFilter = {
  /** Omnisearch text, as typed in the task list's search: free text plus key:value terms. */
  search: string;
  statuses: TaskStatus[];
  facilityIds: string[];
  assigneeIds: string[];
  watcherIds: string[];
  tags: string[];
  facilityTags: string[];
};

/** Matches the task list's default: everything but canceled tasks. */
export const DEFAULT_CALENDAR_FILTER: CalendarFilter = {
  search: "",
  statuses: [TaskStatus.Open, TaskStatus.InProgress, TaskStatus.Completed],
  facilityIds: [],
  assigneeIds: [],
  watcherIds: [],
  tags: [],
  facilityTags: [],
};

const TASK_STATUSES = new Set<string>(Object.values(TaskStatus));
const isTaskStatus = (value: string): value is TaskStatus => TASK_STATUSES.has(value);

/** Keys the dropdowns own; any other key typed in the search stays in the search. */
const DROPDOWN_KEYS = [
  "taskStatus",
  "facilityId",
  "assigneeId",
  "watcherId",
  "tags",
  "facilityTags",
] as const;
const RESERVED_KEYS = new Set<string>(CALENDAR_FEED_RESERVED_KEYS);

/**
 * The omnisearch string the calendar feed runs for a custom filter. Typed terms
 * for a key a dropdown also sets are merged with the dropdown's values.
 */
export function toCalendarFilterSearch(filter: CalendarFilter): string {
  const dropdownPairs: OmnisearchPair[] = [
    { key: "taskStatus", values: filter.statuses },
    { key: "facilityId", values: filter.facilityIds },
    { key: "assigneeId", values: filter.assigneeIds },
    { key: "watcherId", values: filter.watcherIds },
    { key: "tags", values: filter.tags },
    { key: "facilityTags", values: filter.facilityTags },
  ].filter((pair) => pair.values.length > 0);
  // splitOmnisearch merges repeated keys, so typed terms join the dropdowns' values.
  const { pairs, freeText } = splitOmnisearch(
    `${serializeOmnisearch(dropdownPairs, "")} ${filter.search}`
  );
  return serializeOmnisearch(
    pairs.filter((pair) => !RESERVED_KEYS.has(pair.key)),
    freeText
  );
}

/** A saved custom filter back as dropdown selections plus the rest as search text. */
export function parseCalendarFilterSearch(search: string): CalendarFilter {
  const { pairs, freeText } = splitOmnisearch(search);
  const dropdownKeys = new Set<string>(DROPDOWN_KEYS);
  const valuesFor = (key: string) => pairs.find((pair) => pair.key === key)?.values ?? [];
  return {
    search: serializeOmnisearch(
      pairs.filter((pair) => !dropdownKeys.has(pair.key)),
      freeText
    ),
    statuses: valuesFor("taskStatus").filter(isTaskStatus),
    facilityIds: valuesFor("facilityId"),
    assigneeIds: valuesFor("assigneeId"),
    watcherIds: valuesFor("watcherId"),
    tags: valuesFor("tags"),
    facilityTags: valuesFor("facilityTags"),
  };
}
